import { createContext, useContext } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Lock, ArrowLeft } from 'lucide-react'
import { Spinner } from '../ui/Spinner'
import { UpgradePrompt } from '../ui/UpgradePrompt'
import {
  useCurrentUserPagePermissions,
} from '../../lib/permissions/usePagePermissions'
import { useEnforcement } from '../../hooks/useEnforcement'
import { useTenantAccess } from '../../hooks/useTenantAccess'
import {
  meetsRequiredLevel,
  PAGE_REGISTRY_BY_KEY,
  type PermissionLevel,
} from '../../lib/permissions/pageRegistry'

type GuardContext = {
  level: PermissionLevel
  editable: boolean
}

const PermissionContext = createContext<GuardContext | null>(null)

/**
 * Pages wrapped in <PermissionGuard> can read their access level via
 *   const { editable, level } = usePageAccess()
 * to disable forms / hide action buttons when level is 'view'.
 */
export function usePageAccess(): GuardContext {
  const ctx = useContext(PermissionContext)
  if (!ctx) return { level: 'edit', editable: true }
  return ctx
}

interface PermissionGuardProps {
  pageKey: string
  requiredLevel?: PermissionLevel
  children: React.ReactNode
}

/**
 * Route guard: blocks rendering if the user's level for `pageKey`
 * is below `requiredLevel`. When level is 'view' but `requiredLevel`
 * is 'edit', this guard still renders children but provides
 * `editable=false` to descendants via context.
 */
export function PermissionGuard({
  pageKey,
  requiredLevel = 'view',
  children,
}: PermissionGuardProps) {
  const { getLevel, isLoading, isOwner, isSuperAdmin } = useCurrentUserPagePermissions()
  const { enforced } = useEnforcement()
  const { hasPage, loadingPages } = useTenantAccess()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" />
      </div>
    )
  }

  const fullAccess = (
    <PermissionContext.Provider value={{ level: 'edit', editable: true }}>
      {children}
    </PermissionContext.Provider>
  )

  // 1. Platform super_admin bypasses everything — role AND plan.
  if (isSuperAdmin) return fullAccess

  // 2. Plan gate — "upgrade". Applies to EVERYONE else, owner included: the
  // owner pays for a tier and only sees what that tier includes. Gated by the
  // enforcement flag (kill-switch + per-tenant), so it's a no-op until
  // enforcement is on. Held while plan pages load so we don't flash a prompt.
  const planBlocked = enforced && !loadingPages && !hasPage(pageKey)
  if (planBlocked) {
    return <UpgradePrompt pageKey={pageKey} />
  }

  // 3. Role gate bypass: the owner can open any page within their plan (the
  // plan gate above already applied to them). Everyone else is fail-closed —
  // a role with no explicit row for this page resolves to 'none' and is denied
  // (missing rows are backfilled to 'none' by migration 202).
  if (isOwner) return fullAccess

  // 4. Regular staff — "ask your admin".
  const level = getLevel(pageKey)
  if (!meetsRequiredLevel(level, 'view')) {
    return <NoAccess pageKey={pageKey} />
  }

  const editable = meetsRequiredLevel(level, requiredLevel)

  return (
    <PermissionContext.Provider value={{ level, editable }}>
      {children}
    </PermissionContext.Provider>
  )
}

function NoAccess({ pageKey }: { pageKey: string }) {
  const location = useLocation()
  const entry = PAGE_REGISTRY_BY_KEY[pageKey]
  const pageLabel = entry?.label

  return (
    <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
      <div className="h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
        <Lock className="h-7 w-7 text-slate-400" />
      </div>
      <h1 className="text-lg font-semibold text-slate-900 mb-1">
        You don&apos;t have permission to view this page
      </h1>
      <p className="text-sm text-slate-500 max-w-md">
        Contact your administrator to request access.
      </p>
      {pageLabel && (
        <p className="mt-2 text-xs text-slate-400 max-w-md">
          Tried to open <span className="font-medium text-slate-600">{pageLabel}</span>
          <span className="block font-mono mt-0.5 break-all">{location.pathname}</span>
        </p>
      )}
      <Link
        to="/dashboard"
        className="btn-primary mt-6 inline-flex items-center gap-1.5"
      >
        <ArrowLeft className="h-4 w-4" />
        Go to dashboard
      </Link>
    </div>
  )
}
